import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, EmptyState, Field, Loader, Select } from "../../../../../ui";
import { Callout, Dialog, Tabs } from "../ui-pending";
import { toast } from "react-toastify";
import { allocateTeacherForBooking, listDemoFreeSlots, triggerMixpanelEvent, updateStudentInterestTag, updateUserCampaign } from "../../../../../Api/apiUtils";
import { MixpanelEvents, trackEvent } from "../../../../../utils/mixpanelTracking";
import {
  buildAllocateTeacherPayload,
  expandDemoCourseOptions,
  isDemoCourseAlreadyCompleted,
  isSameDemoCourseSelection,
  normalizeDemoSlotDays,
  resolveCourseTagId,
  toSelectedDemoCourse,
  type DemoSlotDay,
  type DemoSlotItem,
  type DemoSlotPeriod,
} from "./demoSlotUtils";
import "./DemoRescheduleModal.scss";

/** Mixpanel attribution for new-student demo bookings from this admin CMS. */
const ADMIN_DEMO_BOOK_ATTRIBUTION = {
  "UTM Source": "admintool",
} as const;

export type DemoSlotModalMode = "book" | "reschedule";

interface DemoSlotModalProps {
  isOpen: boolean;
  toggle: () => void;
  mode: DemoSlotModalMode;
  profileData: any;
  classScheduleId?: string | number | null;
  eventDetails?: any;
  courses: any[];
  /** Student courses from /admin/courses — disable when parentTagId matches option.id and demo is completed. */
  studentCourses?: any[];
  /** Student id (not credential) — the student is tagged with the picked course before slots load. */
  studentId?: string | number | null;
  onSuccess: (reason?: string) => void;
}

const PERIOD_LABELS: Record<DemoSlotPeriod, string> = {
  morning: "Morning (7am - 11am)",
  afternoon: "Afternoon (12pm - 6pm)",
  evening: "Evening",
};

const RequiredMark = () => <span className="ds-field__required">REQUIRED</span>;

const DemoSlotModal: React.FC<DemoSlotModalProps> = ({
  isOpen,
  toggle,
  mode,
  profileData,
  classScheduleId,
  eventDetails,
  courses,
  studentCourses = [],
  studentId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState("");
  const [selectedCourse, setSelectedCourse] = useState<any>(null);
  const [slotDays, setSlotDays] = useState<DemoSlotDay[]>([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState<DemoSlotItem | null>(null);
  const [activePeriod, setActivePeriod] = useState<DemoSlotPeriod>("morning");
  // latest course click; a slower response from an earlier click must not overwrite it
  const courseSelectRequest = useRef(0);

  const credentialId =
    profileData?.studentCredential ||
    profileData?.credentialId ||
    profileData?.studentCredentialId;

  const studentUserId =
    profileData?.userId ??
    profileData?.studentUserId ??
    (() => {
      try {
        return JSON.parse(localStorage.getItem("studentUserId") || "null");
      } catch {
        return null;
      }
    })();

  const selectedDay = useMemo(
    () => slotDays.find((day) => day.slotDateStr === selectedDate) || null,
    [slotDays, selectedDate]
  );

  const demoCourseOptions = useMemo(() => expandDemoCourseOptions(courses), [courses]);

  const periodSlots = useMemo(() => {
    if (!selectedDay) return [];
    if (activePeriod === "morning") return selectedDay.morningSlots;
    if (activePeriod === "afternoon") return selectedDay.afternoonSlots;
    return selectedDay.eveningSlots;
  }, [selectedDay, activePeriod]);

  const loadSlots = async (course?: any) => {
    if (!credentialId) {
      toast.error("Student credential is missing.");
      return;
    }
    setLoading(true);
    try {
      const data = await listDemoFreeSlots(credentialId, {
        enablePaidTeacher: course?.name === "Tabla",
      });
      const days = normalizeDemoSlotDays(data);
      setSlotDays(days);
      const firstDate = days[0]?.slotDateStr || "";
      setSelectedDate(firstDate);
      setSelectedSlot(null);
      setActivePeriod("morning");
    } catch {
      toast.error("Failed to fetch demo slots");
      setSlotDays([]);
      setSelectedDate("");
      setSelectedSlot(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setReason("");
    setSelectedSlot(null);
    setSelectedDate("");
    setSelectedCourse(null);
    setSlotDays([]);
    setActivePeriod("morning");
  }, [isOpen]);

  const handleCourseSelect = (option: ReturnType<typeof expandDemoCourseOptions>[number]) => {
    if (mode === "book" && isDemoCourseAlreadyCompleted(option, studentCourses)) {
      toast.error("Demo already completed for this course. You cannot book it again.");
      return;
    }
    const course = toSelectedDemoCourse(option);
    setSelectedCourse(course);
    setReason("");
    setSelectedSlot(null);
    setSelectedDate("");
    void tagInterestThenLoadSlots(course);
  };

  // listFreeSlots has no course parameter — it offers slots for the student's current interest
  // tag, so on reschedule the student is tagged with the picked course first (same tag id the
  // booking sends), and slots load only once that succeeds
  const tagInterestThenLoadSlots = async (course: any) => {
    const request = ++courseSelectRequest.current;
    if (!studentId) {
      toast.error("Student id is missing, cannot update the interest.");
      return;
    }
    setLoading(true);
    setSlotDays([]);
    try {
      await updateStudentInterestTag(studentId, resolveCourseTagId(course));
    } catch {
      if (request === courseSelectRequest.current) {
        toast.error("Failed to update the student interest for this course");
        setLoading(false);
      }
      return;
    }
    if (request !== courseSelectRequest.current) return;
    await loadSlots(course);
  };

  const handleConfirm = async () => {
    if (!selectedSlot) {
      toast.error("Please select slot time.");
      return;
    }
    if (mode === "reschedule" && !reason) {
      toast.error("Please select reschedule reason.");
      return;
    }
    if (!credentialId) {
      toast.error("Student credential is missing.");
      return;
    }

    const scheduleId =
      mode === "reschedule"
        ? classScheduleId ||
          eventDetails?.classScheduleId ||
          eventDetails?.classId ||
          ""
        : "";
    const tagId = resolveCourseTagId(
      selectedCourse,
      eventDetails?.tagId || eventDetails?.courseTagId
    );

    const mixpanelEvent =
      mode === "book" ? MixpanelEvents.DEMO_BOOK : MixpanelEvents.DEMO_RESCHEDULE;
    const attribution = mode === "book" ? ADMIN_DEMO_BOOK_ATTRIBUTION : {};

    trackEvent(mixpanelEvent, {
      action: "attempted",
      classScheduleId: scheduleId || undefined,
      classId: eventDetails?.classId,
      selectedSlotId: selectedSlot.uniqueId,
      tagId,
      userId: String(credentialId),
      reason: reason || undefined,
      ...attribution,
    });

    setSubmitting(true);
    const requestStartedAt = performance.now();
    try {
      if (mode === "book" && studentUserId) {
        try {
          await updateUserCampaign(studentUserId, {
            utmSource: "admintool",
            utmCampaign: null,
            utmMedium: null,
            gclid: null,
            utmContent: null,
            utmTerm: null,
            audience: null,
            market: null,
            placement: null,
            referringHostName: "",
            referringUrl: "",
          });
        } catch (campaignError) {
          // Attribution only — a failed campaign call must not block the booking.
          console.error("Campaign update failed before demo book:", campaignError);
        }
      }

      const payload = buildAllocateTeacherPayload({
        mode,
        slot: selectedSlot,
        tagId,
        userId: credentialId,
        ...(mode === "reschedule"
          ? { classScheduleId: scheduleId, rescheduleReason: reason }
          : {}),
      });
      await allocateTeacherForBooking(payload);
      const apiResponseTime = `${(performance.now() - requestStartedAt).toFixed(2)}ms`;

      try {
        const dateTimeString = `${selectedSlot.slotDate || selectedSlot.slotDateStr || ""} ${
          selectedSlot.slotStartTime || ""
        }`.trim();
        const slotDateTime = dateTimeString ? new Date(dateTimeString) : new Date();
        const slotDateTimeInIST = Number.isNaN(slotDateTime.getTime())
          ? new Date().toISOString()
          : slotDateTime.toISOString();
        let loginProfile: { firstName?: string; lastName?: string } = {};
        try {
          loginProfile = JSON.parse(localStorage.getItem("profileData") || "{}");
        } catch {
          loginProfile = {};
        }
        const agentName =
          localStorage.getItem("loginUser") ||
          [loginProfile.firstName, loginProfile.lastName].filter(Boolean).join(" ").trim() ||
          "";
        const countryCode = String(profileData?.countryCode || "");
        const mobileNumber = String(profileData?.mobileNumber || "");

        await triggerMixpanelEvent(
          mode === "book"
            ? MixpanelEvents.DEMO_BOOK
            : MixpanelEvents.DEMO_RESCHEDULE,
          {
            slotDateTimeInIST,
            selectedSlotObj: selectedSlot || {},
            apiResponseTime,
            slotDateInIST: String(selectedSlot.slotDateStr || selectedSlot.slotDate || ""),
            slotTimeInTimezone: String(
              selectedSlot.slotStartTimeStrInGivenTimeZone || ""
            ),
            courseId: selectedCourse?.id || "",
            courseName: selectedCourse?._demoLabel || selectedCourse?.name || "",
            timezone: profileData?.timezone || "",
            agentName,
            timezoneId: profileData?.timezoneId || profileData?.timezone || "",
            phone_number: `${countryCode}${mobileNumber}`,
            userId: studentUserId || profileData?.userId,
            name:`${profileData?.firstName} ${profileData?.lastName}`,
            utm_source: "adminTool",
            rescheduleReason: mode === "reschedule" ? reason : null,
          }
        );
      } catch (mixpanelTriggerError) {
        console.error("Mixpanel trigger failed after demo booking:", mixpanelTriggerError);
      }

      trackEvent(mixpanelEvent, {
        action: "success",
        classScheduleId: scheduleId,
        classId: eventDetails?.classId,
        selectedSlotId: selectedSlot.uniqueId,
        reason: reason || undefined,
        ...attribution,
      });

      toast.success(
        mode === "book"
          ? "Class scheduled successfully."
          : "Demo class rescheduled successfully."
      );
      onSuccess(reason);
      toggle();
    } catch (error: any) {
      const responseError = error?.response?.data;
      const message =
        responseError?.message ||
        (typeof responseError === "string" ? responseError : "") ||
        (mode === "book"
          ? "Failed to book demo class."
          : "Failed to reschedule demo class.");
      trackEvent(mixpanelEvent, {
        action: "failed",
        classScheduleId: scheduleId,
        classId: eventDetails?.classId,
        reason: reason || undefined,
        error:
          typeof responseError === "string"
            ? responseError
            : JSON.stringify(responseError || {}),
        ...attribution,
      });
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Selected course/date/slot is filled with the primary colour (plus a tick on date and
  // slot buttons); styles live in DemoRescheduleModal.scss
  const slotButtonClass = (active: boolean) => `demo-slot-btn${active ? " is-selected" : ""}`;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={toggle}
      size="lg"
      centered={false}
      title="Book your slot"
      actions={
        <>
          <Button variant="secondary" onClick={toggle}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            loading={submitting}
            disabled={
              submitting ||
              loading ||
              !selectedCourse ||
              !selectedSlot ||
              (mode === "reschedule" && !reason)
            }
          >
            {submitting ? "Confirming..." : "Confirm Free Trial"}
          </Button>
        </>
      }
    >
        <div className="demo-slot-section" role="group" aria-labelledby="demo-slot-course-label">
          <span className="ds-field__label" id="demo-slot-course-label">
            Select interested course
            <RequiredMark />
          </span>
          <div className="demo-slot-grid">
            {demoCourseOptions.map((option) => {
              const isSelected = isSameDemoCourseSelection(selectedCourse, option);
              const isCompletedDemo =
                mode === "book" && isDemoCourseAlreadyCompleted(option, studentCourses);
              return (
                <Button
                  key={option.key}
                  className={slotButtonClass(Boolean(isSelected))}
                  aria-pressed={Boolean(isSelected)}
                  disabled={isCompletedDemo}
                  title={
                    isCompletedDemo
                      ? "Demo already completed for this course"
                      : undefined
                  }
                  onClick={() => {
                    if (isCompletedDemo) return;
                    handleCourseSelect(option);
                  }}
                >
                  {option.label}
                  {isCompletedDemo ? " (Completed)" : ""}
                </Button>
              );
            })}
          </div>
        </div>

        {!selectedCourse ? (
          <p className="demo-slot-hint ds-text-subtle">
            Please select a course to view available slots.
          </p>
        ) : (
          <>
            {mode === "reschedule" && (
              <Field label="Select reschedule reason" required>
                {(ctl) => (
                  <Select
                    {...ctl}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    options={[
                      { value: "", label: "Select reason for rescheduling" },
                      { value: "ae not found", label: "AE not found" },
                      { value: "demo class not conducted", label: "Demo class not conducted" },
                      { value: "demo success", label: "Demo success" },
                    ]}
                  />
                )}
              </Field>
            )}

            <Callout tone="warning">
              Note : This slots are showing based on student time zone
            </Callout>

            <div className="demo-slot-section" role="group" aria-labelledby="demo-slot-slot-label">
              <span className="ds-field__label" id="demo-slot-slot-label">
                Select slot
                <RequiredMark />
              </span>

              {loading ? (
                <Loader label="Loading slots…" />
              ) : slotDays.length === 0 ? (
                <EmptyState
                  title="No slots available"
                  description="No demo slots are open for this student right now. Try another course or check again later."
                />
              ) : (
                <>
                  <div className="demo-slot-dates">
                    {slotDays.map((day) => (
                      <Button
                        key={day.slotDateStr}
                        className={slotButtonClass(selectedDate === day.slotDateStr)}
                        aria-pressed={selectedDate === day.slotDateStr}
                        onClick={() => {
                          setSelectedDate(day.slotDateStr);
                          setSelectedSlot(null);
                          setActivePeriod("morning");
                        }}
                      >
                        {selectedDate === day.slotDateStr ? "✓ " : ""}
                        {day.slotDateStr}
                      </Button>
                    ))}
                  </div>

                  <Tabs
                    ariaLabel="Time of day"
                    value={activePeriod}
                    onChange={(period) => {
                      setActivePeriod(period as DemoSlotPeriod);
                      setSelectedSlot(null);
                    }}
                    items={(Object.keys(PERIOD_LABELS) as DemoSlotPeriod[]).map((period) => ({
                      id: period,
                      label: PERIOD_LABELS[period],
                    }))}
                  />

                  {periodSlots.length === 0 ? (
                    <EmptyState
                      title="No slots available"
                      description="Try another time of day or date."
                    />
                  ) : (
                    <div className="demo-slot-grid">
                      {periodSlots.map((slot) => {
                        const isSelected = selectedSlot?.uniqueId === slot.uniqueId;
                        return (
                          <Button
                            key={slot.uniqueId}
                            className={slotButtonClass(isSelected)}
                            aria-pressed={isSelected}
                            onClick={() => setSelectedSlot(slot)}
                          >
                            {isSelected ? "✓ " : ""}
                            {slot.slotStartTimeStrInGivenTimeZone || slot.slotStartTime}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
    </Dialog>
  );
};

export default DemoSlotModal;
